import { useMemo } from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useCache } from '../contexts/CacheContext';
import { planLimitationsService, PlanLimitations } from '../services/planLimitationsService';
import { LocalUsageStats } from '../services/localUsageService';

export const usePlanLimitations = () => {
  const { state: subscriptionState } = useSubscription();
  const { cache } = useCache();

  const limitations = useMemo((): PlanLimitations | null => {
    if (!subscriptionState.currentSubscription || !cache.offices || !cache.clients || !cache.legalFiles) {
      return null;
    }

    // Calculate usage stats from cache
    const usageStats: LocalUsageStats = {
      userId: subscriptionState.currentSubscription.userId,
      officesCount: cache.offices.length,
      clientsCount: cache.clients.length,
      filesCount: cache.legalFiles.length,
      teamMembersCount: cache.roles?.length || 0,
      lastUpdated: new Date().toISOString(),
      officeUsage: [] // We'll calculate this if needed
    };

    try {
      return planLimitationsService.calculatePlanLimitations(
        subscriptionState.currentSubscription.planId,
        usageStats
      );
    } catch (error) {
      console.error('Error calculating plan limitations:', error);
      return null;
    }
  }, [subscriptionState.currentSubscription, cache.offices, cache.clients, cache.legalFiles, cache.roles]);

  const isActionAllowed = (actionType: 'create_office' | 'create_client' | 'create_file' | 'add_team_member') => {
    if (!subscriptionState.currentSubscription || !limitations) return false;

    const currentCount = (() => {
      switch (actionType) {
        case 'create_office':
          return cache.offices?.length || 0;
        case 'create_client':
          return cache.clients?.length || 0;
        case 'create_file':
          return cache.legalFiles?.length || 0;
        case 'add_team_member':
          return cache.roles?.length || 0;
        default:
          return 0;
      }
    })();

    return planLimitationsService.isActionAllowed(
      subscriptionState.currentSubscription.planId,
      actionType,
      currentCount
    );
  };

  const getDisabledItems = (allItems: any[], itemType: 'office' | 'client' | 'file' | 'team_member') => {
    if (!subscriptionState.currentSubscription || !limitations) {
      return { enabled: allItems, disabled: [] };
    }

    const usageStats: LocalUsageStats = {
      userId: subscriptionState.currentSubscription.userId,
      officesCount: cache.offices?.length || 0,
      clientsCount: cache.clients?.length || 0,
      filesCount: cache.legalFiles?.length || 0,
      teamMembersCount: cache.roles?.length || 0,
      lastUpdated: new Date().toISOString(),
      officeUsage: []
    };

    return planLimitationsService.getDisabledItems(
      subscriptionState.currentSubscription.planId,
      usageStats,
      allItems,
      itemType
    );
  };

  const getLimitationMessage = (itemType: 'office' | 'client' | 'file' | 'team_member') => {
    if (!subscriptionState.currentSubscription || !limitations) return '';

    const usageStats: LocalUsageStats = {
      userId: subscriptionState.currentSubscription.userId,
      officesCount: cache.offices?.length || 0,
      clientsCount: cache.clients?.length || 0,
      filesCount: cache.legalFiles?.length || 0,
      teamMembersCount: cache.roles?.length || 0,
      lastUpdated: new Date().toISOString(),
      officeUsage: []
    };

    return planLimitationsService.getLimitationMessage(
      subscriptionState.currentSubscription.planId,
      usageStats,
      itemType
    );
  };

  const shouldShowUpgradePrompt = () => {
    if (!subscriptionState.currentSubscription || !limitations) return false;

    const usageStats: LocalUsageStats = {
      userId: subscriptionState.currentSubscription.userId,
      officesCount: cache.offices?.length || 0,
      clientsCount: cache.clients?.length || 0,
      filesCount: cache.legalFiles?.length || 0,
      teamMembersCount: cache.roles?.length || 0,
      lastUpdated: new Date().toISOString(),
      officeUsage: []
    };

    return planLimitationsService.shouldShowUpgradePrompt(
      subscriptionState.currentSubscription.planId,
      usageStats
    );
  };

  const getSuggestedUpgradePlan = () => {
    if (!subscriptionState.currentSubscription) return null;
    return planLimitationsService.getSuggestedUpgradePlan(subscriptionState.currentSubscription.planId);
  };

  return {
    limitations,
    isActionAllowed,
    getDisabledItems,
    getLimitationMessage,
    shouldShowUpgradePrompt,
    getSuggestedUpgradePlan
  };
};
